import TableHeaderControls from "@components/Project/TableHeaderControls";
import AdvancedSearch from "@components/Search/AdvancedSearch";
import { useAdvancedSearch } from "@hooks/useAdvancedSearch";
import { resolveSearchCriteria } from "@utils/searchHelpers";
import TableWithPagination from "@components/Tables/TableWithPagination";
import ConfirmDeleteModal from "@components/UI/ConfirmDeleteModal";
import queryKeys from "@configs/react-query/query-key";
import { useMutationData } from "@hooks/useMutationData";
import { useSearchInfo } from "@hooks/useSearchInfo";
import { useTheme } from "@hooks/useTheme";
import { formatDateTime, formatDateForRequest } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { getIndexDBData, STORES, deleteDataInIndexedDb } from "@utils/indexedDb";
import { refreshPaginatedListAfterDelete } from "@utils/pagination";
import { useCallback, useEffect, useRef, useState, useMemo, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import ToastConstant from "@configs/react-toastify/snackbar";
import { TOAST } from "@constants/index";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import { PermissionContext } from "@contexts/PermissionContext";
import { useRoutePermissions } from "@contexts/RoutePermissionContext";
import useItemsPerPage from "@hooks/useItemsPerPage";
import { useDataSyncRefresh } from "@hooks/useDataSyncRefresh";

const NoticeInformationList = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { hasDeletePermission, hasEditPermission } = useRoutePermissions();
  const { canManageProjectWorkflow } = useContext(PermissionContext);
  const [noticeInformationList, setNoticeInformationList] = useState([]);
  const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.NOTICE_INFORMATION_LIST, DEFAULT_ITEMS_PER_PAGE);
  const [searchFilters, setSearchFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalData, setTotalData] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showModalDelete, setShowModalDelete] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
  const [isNoticeInformationListLoading, setIsNoticeInformationListLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");

  const navigate = useNavigate();
  const isFirstRender = useRef(true);

  const { departmentList, noticeStatusList, creatorList, fetchSearchInfo } = useSearchInfo();

  const translatedNoticeStatusList = useMemo(() => {
    if (!noticeStatusList) return [];

    return noticeStatusList.map((item) => ({
      value: item.value,
      label: t(
        `project.notice-infor.notice-status-${item.label?.toLowerCase()}`,
        item.label
      ),
    }));
  }, [noticeStatusList, t]);

  const searchFields = [
    {
      name: "noticeInfoId",
      label: t("project.notice-infor.notice-infor-id"),
      placeholder: t("project.notice-infor.notice-infor-id"),
    },
    {
      name: "noticeInfoStatusCd",
      label: t("project.notice-infor.notice-infor-status"),
      type: "select",
      keepRawLabel: true,
      options: translatedNoticeStatusList,
      placeholder: t("project.notice-infor.notice-infor-status"),
    },
    {
      name: "createrId",
      label: t("project.notice-infor.creator"),
      type: "select",
      keepRawLabel: true,
      options: creatorList,
      displayFormatLabelWithID: true,
      placeholder: t("project.notice-infor.creator"),
    },
    // {
    //   name: "createrDeptCd",
    //   label: t("project.notice-infor.creator-department"),
    //   type: "select",
    //   keepRawLabel: true,
    //   options: departmentList,
    //   placeholder: t("project.notice-infor.creator-department"),
    // },
    {
      name: "createdAtFrom",
      label: t("common.create-at-from"),
      placeholder: t("common.create-at-from"),
      type: "date",
    },
    {
      name: "createdAtTo",
      label: t("common.create-at-to"),
      placeholder: t("common.create-at-to"),
      type: "date",
    },
  ];
  const { showAdvancedSearch, handleAdvancedSearchToggle } = useAdvancedSearch({
    searchFields: searchFields,
    quickSearchField: "search",
    setSearchCriteria: setSearchFilters,
  });

  useEffect(() => {
    fetchData();
  }, [showAdvancedSearch]);


  const columns = [
    "noticeInfoId",
    "noticeInfoName",
    "noticeInfoStatusCdName",
    // "createrDeptCdName",
    "createrName",
    "createdAt",
  ];

  const columnConfig = {
    noticeInfoId: {
      headerRender: () => t("project.notice-infor.notice-infor-id"),
    },
    noticeInfoName: {
      headerRender: () => t("project.notice-infor.notice-infor-name"),
    },

    noticeInfoStatusCdName: {
      headerRender: () => t("project.notice-infor.notice-infor-status"),
      customRender: (value, row) => renderNoticeStatusCell(value),
    },
    // createrDeptCdName: {
    //   headerRender: () => t("project.notice-infor.creator-department"),
    //   customRender: (value, row) => {
    //     return <div>{row.createrDeptCdName || "-"}</div>;
    //   },
    // },
    createrName: {
      headerRender: () => t("project.notice-infor.creator"),
      customRender: (value, row) => {
        return <UserNameWithId name={row.createrName} id={row.createrId} />;
      },
    },
    createdAt: {
      headerRender: () => t("common.create-at"),
      customRender: (value) => formatDateTime(value),
    },
  };

  // Status color logic (match ProjectList)
  const renderNoticeStatusCell = (status) => {
    const STATUS = {
      "WRITING": "Writing...",
      "REQUEST_WRITING": "Request Writing",
      "REVIEWING": "Reviewing...",
      "NOTICE_COMPLETED": "Notice Completed",
    }

    const statusStyles = {
      [STATUS.WRITING]: colors.textStatusAnalysis,
      [STATUS.REQUEST_WRITING]: colors.textStatusVerification,
      [STATUS.REVIEWING]: colors.textStatusReview,
      [STATUS.NOTICE_COMPLETED]: colors.textStatusSuccess,
    };
    const statusColorDot = {
      [STATUS.WRITING]: colors.bgStatusAnalysis,
      [STATUS.REQUEST_WRITING]: colors.bgStatusVerification,
      [STATUS.REVIEWING]: colors.bgStatusReview,
      [STATUS.NOTICE_COMPLETED]: colors.bgStatusSuccess,
    };

    return (
      <span
        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-sm font-semibold ${statusStyles[status] || colors.badgeVariantDefault}`}
      >
        <div className={`mr-2 h-2 w-2 rounded-full ${statusColorDot[status]}`}></div>
        { t(`project.notice-infor.notice-status-${status?.toLowerCase() || ''}`, status) }
      </span>
    );
  };

  // Controls
  const handleDelete = () => setShowModalDelete(true);
  const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleItemsPerPageChange(newItemsPerPage);
    setCurrentPage(1); // Reset to page 1 when changing number of rows
  };

  const canManageNotice = useCallback(
    (row) => canManageProjectWorkflow(row?.projectManagerId),
    [canManageProjectWorkflow],
  );

  const handleCheckboxChange = (newSelectedRows) => setSelectedRows([...newSelectedRows]);
  const onRowClick = (rowData) => navigate(`./${rowData.noticeInfoSeq}`);

  // Search handler
  const searchHandler = useCallback((inputData) => {
    setSearchFilters((prev) => ({
      ...prev,
      ...inputData,
    }));
  }, []);

  const handleSort = (columnKey, direction) => {
    setSortColumn(columnKey);
    setSortDirection(direction);
  };

  const deleteNoticeInfos = useMutationData(
    queryKeys.deleteNoticeInfos,
    { method: "DELETE" },
    {
      onSuccess: async () => {
        await deleteDataInIndexedDb(STORES.NOTICE_INFOS, selectedRows);
        ToastConstant(TOAST.success, t("project.notice-infor.notice-infor-delete-success"));
        refreshPaginatedListAfterDelete({
          currentPage,
          setCurrentPage,
          itemsPerPage,
          totalData,
          deletedCount: selectedRows.length,
          fetchData,
        });
        setSelectedRows([]);
      },
      onError: (error) => {
        console.error("Error during delete data:", error);
        ToastConstant(TOAST.error, t("toast.verification-report.delete-failed.title"), t("verification-report.delete-failed.message"));
      },
    },
  );

  const getListData = async (bodyRequest = {}) => {
    setIsNoticeInformationListLoading(true);
    try {
      const {
        pageSize = itemsPerPage,
        startRow = 0,
        sortKey = sortColumn || "createdAt",
        sortOrder = sortDirection || "desc",
        ...filters
      } = bodyRequest;
      const response = await getIndexDBData(STORES.NOTICE_INFOS, {
        pageSize,
        startRow,
        filters,
        sortKey,
        sortOrder,
      });

      if (response && typeof response !== "string") {
        setNoticeInformationList(response.object || []);
        const totalCount = response.objectCount || 0;
        setTotalData(totalCount);
        setTotalPages(Math.ceil(totalCount / (pageSize || 1)));
      }
    } catch (error) {
      console.error("Error fetching notice informations from IndexedDB:", error);
      ToastConstant(TOAST.error, t("toast.sbom.fetch-error.title"), t("toast.sbom.fetch-error.message"));
    } finally {
      setIsNoticeInformationListLoading(false);
    }
  };

  // --- RESTORED FETCH LOGIC ---
  useEffect(() => {
    fetchSearchInfo && fetchSearchInfo();
  }, []);

  useEffect(() => {
    fetchData();
  }, [currentPage]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      fetchData();
    }
  }, [searchFilters, itemsPerPage, sortColumn, sortDirection]);

  const fetchData = () => {
    const effectiveCriteria = resolveSearchCriteria(searchFilters, {
      quickSearchField: "search",
      isAdvancedPanelOpen: showAdvancedSearch,
    });

    const bodyRequest = Object.keys(effectiveCriteria).reduce((acc, key) => {
      let value = effectiveCriteria[key];
      if (typeof value === "string") {
        value = value.trim();
      }
      if (value !== "" && value !== undefined) {
        acc[key] = formatDateForRequest(value);
      }
      return acc;
    }, {});
    bodyRequest["pageSize"] = itemsPerPage;
    bodyRequest["startRow"] = itemsPerPage * (currentPage - 1);
    bodyRequest["sortKey"] = sortColumn || "createdAt";
    bodyRequest["sortOrder"] = sortDirection || "desc";
    getListData(bodyRequest);
  };

  // Auto-refresh data when sync completes
  useDataSyncRefresh(fetchData);

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteNoticeInfos.mutateAsync(selectedRows);
      setSelectedRows([]);
      setShowModalDelete(false);
    } catch (error) {
      console.error("Error deleting notice infos:", error);
      // Optionally show a toast here
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className={`flex flex-col ${colors.bgSecondary}`} data-testid="notice-information-list-page">
      {/* Header */}
      <div className={`${colors.textPlaceholder} py-1`} data-testid="notice-information-list-header">
        <div className="flex items-center justify-between">
          <h1 className={`text-[28px] uppercase font-bold ${colors.textPrimary}`} data-testid="notice-information-list-title">{t("project.notice-infor.notice-infor-list")}</h1>
        </div>
      </div>

      {/* Controls */}
      <div className={`flex-shrink-0 pt-4 ${colors.bgSecondary}`} data-testid="notice-information-list-controls">
        <TableHeaderControls
          controlsTestId="notice-information-list-table-header-controls"
          searchInputTestId="notice-information-list-search-input"
          addButtonTestId="notice-information-list-add-button"
          deleteButtonTestId="notice-information-list-delete-button"
          advancedSearchButtonTestId="notice-information-list-advanced-search-button"
          onAdvancedSearchToggle={handleAdvancedSearchToggle}
          isAdvancedPanelOpen={showAdvancedSearch}
          onDeleteProject={handleDelete}
          searchField="search"
          searchValue={searchFilters.search ?? ""}
          onSearch={searchHandler}
          hasSelectedRows={selectedRows.length > 0}
          placeholderSearch={t("common.search-placeholder")}
          titleDeleteButton={t("project.notice-infor.delete-notice")}
          isDisplayAddButton={hasEditPermission}
          titleAddButton={t("project.notice-infor.add-notice")}
          isDisplayDeleteButton={hasDeletePermission}
          onAdd={() => navigate("/notice-information/new")}
        />
      </div>

      {/* Advanced Search */}
      {showAdvancedSearch && (
        <div className="mb-4" data-testid="notice-information-list-advanced-search">
          <AdvancedSearch
            data-testid="notice-information-list-advanced-search-panel"
            title={t("project.advancedSearch")}
            searchFields={searchFields}
            searchHandler={searchHandler}
            withoutSearch={true}
          />
        </div>
      )}

      {/* Table */}
      <div className="flex-grow min-h-0" data-testid="notice-information-list-table">
        <TableWithPagination
          columnConfig={columnConfig}
          data={noticeInformationList}
          columns={columns}
          onRowClick={onRowClick}
          loading={isNoticeInformationListLoading}
          totalPages={totalPages}
          totalData={totalData}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          maxRowsPerPage={itemsPerPage}
          selectedRows={selectedRows}
          onCheckboxChange={handleCheckboxChange}
          disabledRow={[]}
          hasCheckbox={hasDeletePermission}
          isCheckboxDisabled={(row) => !canManageNotice(row)}
          hasRadioButton={false}
          rowKey="noticeInfoSeq"
          shouldShowGoToPage={true}
          onItemsPerPageChange={handleItemsPerPageChangeWithReset}
          showItemsPerPageSelector={true}
          onSort={handleSort}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          sortable={true}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        data-testid="notice-information-list-delete-modal"
        isOpen={showModalDelete}
        onClose={() => setShowModalDelete(false)}
        onConfirm={handleConfirmDelete}
        title={t("project.notice-infor.confirm-delete-notice-title")}
        description={t("project.notice-infor.confirm-delete-notice-body", { count: selectedRows.length })}
        loading={deleteLoading}
        cancelText={t("common.cancel-button")}
        confirmText={t("common.delete-button")}
      />
    </div>
  );
};

export default NoticeInformationList;
